import { FeatureToggles } from '@/features/feature-toggles'
import { type Stripe, type StripeCardElement } from '@stripe/stripe-js'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { PaymentRequired } from 'fejl'
import { type Member } from '@/features/iam/members/Member'
import { type Team } from '@/features/iam/teams/Team'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { extractMessageFromError } from '@/utils/error-util'
import { required, validationContext } from 'validx'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { toStream } from '@/utils/mobx-util'
import { tap } from 'rxjs'

/**
 * Card payment method editor view model.
 * Single use only.
 */
export class CardPaymentMethodEditorViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The Stripe card element, needed to create a token.
   */
  card: StripeCardElement | null = null

  /**
   * Stripe instance.
   */
  @observable.ref accessor stripe: Stripe | null = null

  /**
   * Full name on the card.
   * Default to the current member's display name.
   */
  @observable accessor fullName: string

  /**
   * Address.
   */
  @observable accessor address = ''

  /**
   * Address city.
   */
  @observable accessor city = ''

  /**
   * State, if applicable.
   */
  @observable accessor stateOrTerritory = ''

  /**
   * Country code.
   */
  @observable accessor country = 'US'

  /**
   * Zip code.
   */
  @observable accessor zipCode = ''

  /**
   * Validation.
   */
  readonly validation = validationContext(this, {
    fullName: [required('Name is required')],
    address: [required('Address is required')],
    city: [required('City is required')],
    country: [required('Country is required')],
    zipCode: [required('Zip code is required')],
    stateOrTerritory: [
      (ctx) =>
        this.isStateOrTerritoryRelevant
          ? required('State is required')(ctx)
          : true,
    ],
  })

  constructor(
    private readonly _member: Member,
    private readonly _team: Team | null,
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _onCreated: (method: BillingMethod) => void,
  ) {
    this.fullName = this._member.displayName

    this._clearValidationOnChange('fullName')
    this._clearValidationOnChange('address')
    this._clearValidationOnChange('city')
    this._clearValidationOnChange('stateOrTerritory')
    this._clearValidationOnChange('country')
    this._clearValidationOnChange('zipCode')
  }

  /**
   * Whether the state/territory field is relevant.
   */
  @computed
  get isStateOrTerritoryRelevant() {
    return this.country === 'US'
  }

  /**
   * Whether the new Stripe Payment Methods flow is enabled.
   */
  @computed
  get isStripePaymentMethodsEnabled() {
    return FeatureToggles.stripePaymentMethods()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The dialog renders a failed load from the task's state.
    void this.prepare(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Prepares the editor by loading the Stripe SDK.
   */
  prepare = task(async (signal?: AbortSignal) => {
    const stripe = await import('@/features/billing/stripe/load-stripe').then(
      (m) => {
        signal?.throwIfAborted()
        return m.getStripe()
      },
    )
    signal?.throwIfAborted()
    runInAction(() => {
      this.stripe = stripe
    })
  })

  /**
   * Creates a billing method from the card.
   */
  save = task.resolved(async () => {
    if (!this._validate()) {
      return
    }

    const method = this.isStripePaymentMethodsEnabled
      ? await this._createBillingMethod()
      : await this._createBillingMethodLegacy()
    if (!method) {
      return
    }

    this._onCreated(method)
    return method
  })

  /**
   * Sets the state or territory, ensures only 2 characters and uppercase.
   *
   * @param value
   */
  @action.bound
  setStateOrTerritory(value: string) {
    const upped = value.toUpperCase()
    if (value && !/[A-Z]/.test(upped)) {
      return
    }

    if (upped.length > 2) {
      return
    }

    this.stateOrTerritory = upped
  }

  /**
   * Sets the country.
   */
  @action.bound
  setCountry(value: string) {
    this.country = value
  }

  /**
   * Sets the Stripe card element, needed to create a token.
   */
  @action.bound
  setCard(card: StripeCardElement) {
    this.card = card
  }

  /**
   * Sets the error the card element reports, or clears it when empty.
   */
  @action.bound
  setCardError(error: string) {
    this.validation.clearErrors('card')
    if (error) {
      this.validation.addErrors({ card: [error] })
    }
  }

  /**
   * Sets up automatic clearing of validation errors when the field changes.
   */
  private _clearValidationOnChange(field: keyof this) {
    this._lifecycle.manage(
      toStream(() => this[field]).pipe(
        tap(() => this.validation.clearErrors(field as string)),
      ),
    )
  }

  @action.bound
  private _validate() {
    this.validation.reset()
    return this.validation.validate().isValid
  }

  @action.bound
  private async _createBillingMethodLegacy() {
    if (!this.stripe || !this.card) {
      throw new TypeError('Stripe was not loaded.')
    }

    const result = await this.stripe.createToken(this.card, {
      name: this.fullName,
      address_line1: this.address,
      address_city: this.city,
      address_country: this.country,
      address_state: this.isStateOrTerritoryRelevant
        ? this.stateOrTerritory
        : '',
      address_zip: this.zipCode,
    })

    if (result.error) {
      this.validation.addErrors({
        card: [result.error.message || 'There was a problem with your card.'],
      })
      return null
    }

    return this._billingProfileStore
      .addBillingMethodForStripeToken(
        this._member,
        this._team,
        Id.assert(result.token?.id),
      )
      .catch((err: unknown) => {
        if (this._maybeSniffMeaningfulInfoFromError(err)) {
          return null
        }
        throw err
      })
  }

  @action.bound
  private async _createBillingMethod() {
    if (!this.stripe || !this.card) {
      throw new TypeError('Stripe was not loaded.')
    }

    const setupIntent =
      await this._billingProfileStore.setupStripePaymentMethodIntent(
        this._member,
        this._team,
      )

    const confirmation = await this.stripe.confirmCardSetup(
      setupIntent.clientSecret,
      {
        payment_method: {
          card: this.card,
          billing_details: {
            name: this.fullName,
            address: {
              city: this.city,
              country: this.country,
              line1: this.address,
              state: this.isStateOrTerritoryRelevant
                ? this.stateOrTerritory
                : '',
              postal_code: this.zipCode,
            },
          },
        },
      },
    )

    if (confirmation.error) {
      this.validation.addErrors({
        card: [
          confirmation?.error?.message || 'There was a problem with your card.',
        ],
      })
      return null
    }

    return this._billingProfileStore.completeSetupStripePaymentMethodIntent(
      confirmation.setupIntent.id,
      this._member,
      this._team,
    )
  }

  /**
   * Makeshift way of figuring out what the server error pertains to,
   * and assign the error to the right field in the validation context.
   *
   * @param err
   */
  @action.bound
  private _maybeSniffMeaningfulInfoFromError(err: unknown) {
    const message = extractMessageFromError(err)
    if (/zip code/i.test(message)) {
      this.validation.addErrors({ zipCode: [message] })
      return true
    }

    if (/card/i.test(message) || err instanceof PaymentRequired) {
      this.validation.addErrors({ card: [message] })
      return true
    }

    return false
  }
}
